import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { createUser } from "@/services/userService";
import { toast } from "sonner";

interface UserFormProps {
    onSuccess?: () => void;
}

export function UserForm({ onSuccess }: UserFormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userData, setUserData] = useState({
        email: "",
        password: "",
        givenName: "",
        familyName: "",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUserData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await createUser({
                username: userData.email, // Use email as username
                email: userData.email,
                password: userData.password,
                givenName: userData.givenName || undefined,
                familyName: userData.familyName || undefined,
            });

            toast.success("User created successfully");

            // Reset form
            setUserData({
                email: "",
                password: "",
                givenName: "",
                familyName: "",
            });

            // Call onSuccess callback if provided
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            toast.error(`Failed to create user: ${(error as Error).message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
                <Label htmlFor="email">Email*</Label>
                <Input
                    id="email"
                    name="email"
                    type="email"
                    value={userData.email}
                    onChange={handleChange}
                    required
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="password">Password*</Label>
                <Input
                    id="password"
                    name="password"
                    type="password"
                    value={userData.password}
                    onChange={handleChange}
                    required
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="givenName">Given Name</Label>
                    <Input
                        id="givenName"
                        name="givenName"
                        value={userData.givenName}
                        onChange={handleChange}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="familyName">Family Name</Label>
                    <Input
                        id="familyName"
                        name="familyName"
                        value={userData.familyName}
                        onChange={handleChange}
                    />
                </div>
            </div>

            <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create User"}
            </Button>
        </form>
    );
}
